import React, { useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useNavigate } from "react-router";
import { runSpecialRequest } from "../../../functions/api";
import ButtonElement from "../../common/ButtonElement";
import CardWithShadow from "../../common/CardWithShadow";
import HtmlElement from "../../common/HtmlElement";
import TermsAndConditionsField from "../../common/TermsAndConditionsField";
import { AppContext } from "../../core/ApplicationContextProvider";
import TextField from "../../dynamic-form/TextField";
import { getValidationsObject } from "../../dynamic-form/validationUtils";
import "./Authentication.css";
import type { AppContextType } from "../../core/types";

const Authentication: React.FC = () => {
  const _NNDirectCompaniesLoginName = "NNDirectCompaniesLogin";

  const {
    authenticate,
    details,
    setApplicationError,
    token,
    loader,
    resolveScreenAction,
  } = React.useContext(AppContext) as AppContextType;

  const phoneNumber = details?.businessLineDetails?.phoneNumber || "";

  const tokenBlockedError = useMemo(
    () => (
      <span>
        Autentificarea nu a putut fi efectuată, pentru a continua procesul te
        rugăm apelează numărul{" "}
        <span style={{ whiteSpace: "nowrap" }}>{phoneNumber}</span>
      </span>
    ),
    [phoneNumber]
  );

  const [agreement, setAgreement] = useState<boolean>(false);
  const [agreementError, setAgreementError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<{ message?: string | React.ReactNode } | null>(null);

  const navigate = useNavigate();

  const form = useForm();
  const { register, handleSubmit, formState: { errors } } = form;

  const auth = async (data: { validationKey?: string }) => {
    if (!validateForm()) return false;

    setLoading(true);
    setAgreementError(false);
    try {
      if (token && data.validationKey) {
        await authenticate(token, data.validationKey);
        await responseFunc();
      }
    } catch (err: any) {
      errorFunc(err);
    }
  };

  const validateForm = (): boolean => {
    if (authPage?.showTermsAndConditionsField && !agreement) {
      setAgreementError(true);
      return false;
    }

    return true;
  };

  const responseFunc = async () => {
    setError(null);

    if (!details?.businessLineDetails || !details?.campaignDetails || !token) return;

    const businessLine = details.businessLineDetails.businessLine?.toLowerCase();
    const campaignName = details.campaignDetails.name.toLowerCase();

    // This depends on `currentScreen` already being set, and it is the only one
    // guaranteed for this campaign.
    if (campaignName === _NNDirectCompaniesLoginName.toLowerCase()) {
      try {
        // Sets the participant as completed in DB
        await resolveScreenAction({});
        // Redirects user to NNDirectCompanies website
        const runSpecialResponse = await runSpecialRequest(token);
        if (runSpecialResponse.status === 200) {
          setLoading(false);
          window.location.replace(runSpecialResponse.data);
        } else {
          throw new Error(
            "Run special request returned " + runSpecialResponse.status
          );
        }
      } catch (err) {
        setLoading(false);
        loader.setError();
        setApplicationError();
      }
    } else {
      setLoading(false);
      if (businessLine) {
        navigate(`/${businessLine}/token/${token}/${campaignName}/complete`, {
          replace: true,
        });
      }
    }
  };

  const errorFunc = (error: any) => {
    setLoading(false);
    const title =
      error.response && error.response.data && error.response.data.title;
    if (title === "TokenBlockedException") {
      setError({ message: tokenBlockedError });
    } else if (title === "InvalidLoginException") {
      setError({ message: invalidLoginError });
    } else {
      setApplicationError();
    }
  };

  const setAgreementValue = () => {
    setAgreementError(agreement);
    setAgreement(!agreement);
  };

  const authPage = details?.campaignDetails?.authenticationPage;
  if (!authPage) {
    return <></>;
  }
  const invalidLoginError = authPage.invalidLoginError;
  const validationObj = getValidationsObject(authPage.passwordInputField);

  const onInvalid = (errors: any) => console.error(errors);

  return (
    <CardWithShadow noShadowSm={true}>
      <div className="card-body">
        <div className="text-center">
          <h3 className="text-orange">{authPage.title}</h3>
          <HtmlElement className="mb-4 subtitle" elements={authPage.subtitle} />
        </div>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(auth, onInvalid)}>
            <div>
              <TextField
                field={{
                  name: "validationKey",
                  type: "string",
                  label: authPage.passwordInputField.label,
                  placeholder: authPage.passwordInputField.label,
                }}
                name="validationKey"
                placeholder={authPage.passwordInputField.label}
                error={
                  error
                    ? { message: typeof error.message === "string" ? error.message : undefined }
                    : errors["validationKey"]
                }
                type={
                  authPage.passwordInputField.type === "password"
                    ? "password"
                    : "text"
                }
                validationsObject={validationObj}
              />
            </div>
            {authPage.showTermsAndConditionsField && (
              <div className="mb-5 pb-2">
                { /* @ts-ignore */}
                <TermsAndConditionsField
                  onClick={setAgreementValue}
                  error={agreementError ? { type: "error", message: "error" } : null}
                  id={"checkbox-warning-filled"}
                  validationsObject={{}}
                  name={"checkbox-warning-filled"}
                />
              </div>
            )}
            <div className="text-center mb-3">
              <ButtonElement
                type="submit"
                fullWidthSm={true}
                disabled={loading}
              />
            </div>
          </form>
        </FormProvider>
      </div>
    </CardWithShadow>
  );
};

export default Authentication;
